import type { Booking, Cleaner } from '@/hooks/queries/use-get-booking.query';
import { useTrack } from '@/features/tracking/useTrack';
import { Link } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { CleanerRatingLabel } from '@/components/shared/cleaner-rating-label';
import { FavoriteCleanerButton } from '@/features/consumer/favorite-cleaner-button';
import { ReviewCleanerForm } from '@/features/consumer/review-cleaner-form';
import { bookingQueryKeys } from '@/lib/booking-query-keys';
import { bookingReviewStatusQueryKey } from '@/hooks/queries/use-get-booking-review-status.query';
import {
  formatBookingDisplayDateWithWeekdayByState,
  formatBookingDisplayTimeByState,
} from '@/lib/date';
import { formatBookingAddress } from '@/lib/address';
import { BookingStatusBadge } from '@/components/shared/booking-status-badge';
import {
  consumerBookingDurationHours,
  formatConsumerDurationFromHours,
} from '@/lib/duration';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useGetBookingDetailQuery } from '@/hooks/queries/use-get-booking-detail.query';
import { useGetBookingReviewStatusQuery } from '@/hooks/queries/use-get-booking-review-status.query';
import { useGetBookingReviewExchangeQuery } from '@/hooks/queries/use-get-booking-review-exchange.query';
import { useGetBookingIssueReportQuery } from '@/hooks/queries/use-get-booking-issue-report.query';
import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import BlueChecked from '/images/icon/checked-blue.png';

function formatDashboardTotalPay(amount: number): string {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '$0';
  const cents = Math.round(n * 100) % 100;
  if (cents === 0) return `$${Math.round(n)}`;
  return `$${n.toFixed(2)}`;
}

/** Aligns with customer app: `total_amount_charged ?? total_amount`. */
function dashboardBookingPayNumber(booking: Booking): number {
  const charged = booking.total_amount_charged;
  if (charged != null && Number.isFinite(Number(charged))) {
    return Number(charged);
  }
  return Number(booking.total_amount);
}

export function formatDashboardBookingAddress(booking: Booking): string {
  return formatBookingAddress(
    booking.address,
    booking.city,
    booking.state,
    booking.zipcode
  );
}

export function hasSubmittedReviewOrTip(cleaner: Cleaner): boolean {
  const tipped = cleaner.tip_amount != null && Number(cleaner.tip_amount) > 0;
  return Boolean(cleaner.has_review || tipped);
}

/** Customer app uses `GET /booking/:id/review-status/` (`review_submitted`); list `has_review` can lag. */
export type BookingReviewStatusAuthority = {
  isLoading: boolean;
  isFetched: boolean;
  cleaners:
    | Array<{
        cleaner_id: string;
        review_submitted: boolean;
        tip_submitted: boolean;
      }>
    | undefined;
  reviewWindowExpired?: boolean;
  issueReportSubmitted?: boolean;
};

function getCleanerReviewMode(
  cleaner: Cleaner,
  authority: BookingReviewStatusAuthority | undefined
): 'form' | 'submitted' {
  if (authority?.reviewWindowExpired) {
    return 'submitted';
  }
  if (
    authority?.isFetched &&
    authority.cleaners &&
    authority.cleaners.length > 0
  ) {
    const row = authority.cleaners.find(
      c => c.cleaner_id === cleaner.cleaner_id
    );
    if (row !== undefined) {
      return row.review_submitted || row.tip_submitted ? 'submitted' : 'form';
    }
  }
  return hasSubmittedReviewOrTip(cleaner) ? 'submitted' : 'form';
}

function CleanerReviewSubmitted({ cleaner }: { cleaner: Cleaner }) {
  const tipVal = cleaner.tip_amount != null ? Number(cleaner.tip_amount) : 0;
  const rating = cleaner.rating ?? 0;
  const comment =
    cleaner.comments != null && String(cleaner.comments).trim() !== ''
      ? String(cleaner.comments).trim()
      : null;

  return (
    <div className="space-y-4">
      <h4 className="text-base font-bold text-black dark:text-white">
        Review for Cleaner
      </h4>
      {comment ? (
        <p className="text-sm font-normal leading-relaxed text-[#666666] dark:text-gray-400">
          {comment}
        </p>
      ) : null}
      <CleanerRatingLabel reviewRating={rating} size="md" />
      {tipVal > 0 ? (
        <p className="text-sm text-[#333333] dark:text-gray-200">
          <span className="text-[#666666] dark:text-gray-400">Tip: </span>
          <span className="font-bold text-black dark:text-white">
            ${tipVal.toFixed(2)}
          </span>
        </p>
      ) : null}
    </div>
  );
}

export type DashboardBookingViewDetailsSearch = Record<
  string,
  string | undefined
>;

type DashboardBookingCardProps = {
  booking: Booking;
  /**
   * `upcoming` / `completed`: fixed badges for dashboard widgets.
   * `history`: badge from booking.status (My Cleanings / customer Completed tab).
   */
  variant: 'upcoming' | 'completed' | 'history';
  /** When false, omit review / tip blocks (e.g. upcoming). */
  showReviewAndTip?: boolean;
  /**
   * History list: show read-only review/tip only for cleaners who already reviewed
   * or tipped (no inline forms). Uses list payload and optional `reviewStatusAuthority`.
   */
  showSubmittedReviewSummary?: boolean;
  /** When set (My Cleanings history), use same source as customer app booking details. */
  reviewStatusAuthority?: BookingReviewStatusAuthority;
  /** Passed to `/bookings/$id` link `search` (e.g. `{ ref: 'dashboard' }`). */
  viewDetailsSearch?: DashboardBookingViewDetailsSearch;
};

export function DashboardBookingCard({
  booking,
  variant: _variant,
  showReviewAndTip = false,
  showSubmittedReviewSummary = false,
  reviewStatusAuthority: reviewStatusAuthorityProp,
  viewDetailsSearch = { ref: 'dashboard' },
}: DashboardBookingCardProps) {
  const queryClient = useQueryClient();
  const cleaners = booking.cleaners ?? [];
  const track = useTrack();
  const isCompletedRow = booking.status.toLowerCase() === 'completed';

  const needsComplianceData =
    isCompletedRow && reviewStatusAuthorityProp === undefined;

  const { data: reviewStatusLocal, isFetched: reviewStatusLocalFetched } =
    useGetBookingReviewStatusQuery(booking.booking_id, {
      enabled: needsComplianceData && !!booking.booking_id,
    });

  const { data: reviewExchangeLocal, isFetched: reviewExchangeLocalFetched } =
    useGetBookingReviewExchangeQuery(booking.booking_id, {
      enabled: needsComplianceData && !!booking.booking_id,
    });

  const { data: issueReportLocal, isFetched: issueReportLocalFetched } =
    useGetBookingIssueReportQuery(booking.booking_id, {
      enabled: needsComplianceData && !!booking.booking_id,
    });

  const reviewStatusAuthority =
    reviewStatusAuthorityProp ??
    (needsComplianceData
      ? {
          isLoading: false,
          isFetched:
            reviewStatusLocalFetched &&
            reviewExchangeLocalFetched &&
            issueReportLocalFetched,
          cleaners: reviewStatusLocal?.cleaners,
          reviewWindowExpired: reviewExchangeLocal?.review_window_expired,
          issueReportSubmitted:
            issueReportLocal?.submitted === true ||
            reviewExchangeLocal?.issue_report_submitted === true,
        }
      : undefined);

  const handleReviewSuccess = () => {
    void queryClient.invalidateQueries({
      queryKey: bookingQueryKeys.all,
      exact: false,
    });
    void queryClient.invalidateQueries({
      queryKey: bookingReviewStatusQueryKey(booking.booking_id),
    });
  };

  const canRateCleaner =
    isCompletedRow &&
    reviewStatusAuthority?.isFetched &&
    (reviewStatusAuthority.cleaners?.some(
      c => !c.review_submitted && !c.tip_submitted
    ) ??
      false) &&
    !reviewStatusAuthority.reviewWindowExpired;
  const issueReportSubmitted = reviewStatusAuthority?.issueReportSubmitted;
  const canReportIssue =
    isCompletedRow &&
    reviewStatusAuthority?.isFetched &&
    !issueReportSubmitted &&
    !reviewStatusAuthority?.reviewWindowExpired;

  return (
    <article
      className={cn(
        'rounded-2xl border border-gray-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900'
      )}
    >
      {/* Header — no rule under title; spacing only */}
      <div className="mb-6 flex items-start justify-between gap-3">
        <h3 className="text-lg font-bold leading-snug text-black dark:text-white md:text-xl">
          {booking.type_of_service}
        </h3>
        <BookingStatusBadge status={booking.status} />
      </div>

      {/* Address + Cleaner */}
      <div className="mb-6 grid gap-8 border-b border-gray-200 pb-6 md:grid-cols-2 dark:border-neutral-800">
        <div>
          <p className="mb-2 text-sm font-normal text-[#666666] dark:text-gray-400">
            Address
          </p>
          <p className="text-sm font-bold leading-relaxed text-black dark:text-white md:text-base">
            {formatDashboardBookingAddress(booking)}
          </p>
        </div>
        <div>
          <p className="mb-2 text-sm font-normal text-[#666666] dark:text-gray-400">
            Cleaner
          </p>
          {cleaners.length === 0 ? (
            <p className="text-sm text-[#666666] dark:text-gray-400">—</p>
          ) : (
            <div className="flex flex-col gap-3">
              {cleaners.map((cleaner, idx) => (
                <div
                  key={cleaner.booked_cleaner_id || idx}
                  className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-900"
                >
                  <img
                    src={cleaner.cleaner_profile_pic || '/images/avatar.png'}
                    alt=""
                    className="h-14 w-14 shrink-0 rounded-full object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-black dark:text-white">
                      {`${cleaner.cleaner_firstname} ${cleaner.cleaner_lastname}`}
                    </p>

                    <p className="text-sm text-[#666666] dark:text-gray-400">
                      {booking.city || booking.state || '—'}
                    </p>
                    {/* BADGE BACKGROUND CHECK */}
                    <div className="bg-blue-200 text-black text-xs flex items-center rounded-sm px-1 py-1 gap-2 w-[170px] my-1">
                      <img src={BlueChecked} alt="" className="h-4" />
                      Background Checked
                    </div>
                    <CleanerRatingLabel
                      averageRating={cleaner.rating}
                      size="sm"
                    />
                  </div>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-[#fafafa] dark:border-neutral-700 dark:bg-neutral-950">
                    <FavoriteCleanerButton cleanerId={cleaner.cleaner_id} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Booking details row */}
      <div className="mb-6 grid grid-cols-2 gap-x-4 gap-y-4 border-b border-gray-200 pb-6 sm:grid-cols-2 md:grid-cols-4 dark:border-neutral-800">
        <div>
          <p className="mb-1 text-sm font-normal text-[#666666] dark:text-gray-400">
            Booking date
          </p>
          <p className="text-sm font-bold text-black dark:text-white">
            {formatBookingDisplayDateWithWeekdayByState(
              booking.booking_date,
              booking.booking_time,
              booking.booking_date_time,
              booking.state
            ) || booking.booking_date}
          </p>
        </div>
        <div>
          <p className="mb-1 text-sm font-normal text-[#666666] dark:text-gray-400">
            Booking time
          </p>
          <p className="text-sm font-bold text-black dark:text-white">
            {formatBookingDisplayTimeByState(
              booking.booking_date,
              booking.booking_time,
              booking.booking_date_time,
              booking.state
            ) || booking.booking_time}
          </p>
        </div>
        <div>
          <p className="mb-1 text-sm font-normal text-[#666666] dark:text-gray-400">
            Duration
          </p>
          <p className="text-sm font-bold text-black dark:text-white">
            {formatConsumerDurationFromHours(
              consumerBookingDurationHours(booking)
            )}
          </p>
        </div>
        <div>
          <p className="mb-1 text-sm font-normal text-[#666666] dark:text-gray-400">
            Total pay
          </p>
          <p className="text-sm font-bold text-black dark:text-white">
            {formatDashboardTotalPay(dashboardBookingPayNumber(booking))}
          </p>
        </div>
      </div>

      {/* Review / tip */}
      {isCompletedRow && showReviewAndTip ? (
        <div className="mb-6 flex flex-col gap-6 border-b border-gray-200 pb-6 dark:border-neutral-800">
          {reviewStatusAuthority &&
          reviewStatusAuthority.isLoading &&
          !reviewStatusAuthority.isFetched ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-7 w-7 animate-spin text-[#ED7034]" />
            </div>
          ) : (
            cleaners.map((cleaner, idx) => {
              const mode = getCleanerReviewMode(cleaner, reviewStatusAuthority);
              if (mode === 'submitted') {
                return (
                  <CleanerReviewSubmitted
                    key={cleaner.booked_cleaner_id || idx}
                    cleaner={cleaner}
                  />
                );
              }
              return (
                <ReviewCleanerForm
                  key={cleaner.booked_cleaner_id || idx}
                  variant="dashboard"
                  cleanerId={cleaner.cleaner_id}
                  bookingId={booking.booking_id}
                  onSuccess={handleReviewSuccess}
                />
              );
            })
          )}
          {cleaners.length === 0 ? (
            <p className="text-sm text-[#666666] dark:text-gray-400">
              No cleaner assigned — open booking details to review when
              available.
            </p>
          ) : null}
        </div>
      ) : null}

      {isCompletedRow && showSubmittedReviewSummary && !showReviewAndTip
        ? (() => {
            const showSummarySpinner =
              reviewStatusAuthority &&
              reviewStatusAuthority.isLoading &&
              !reviewStatusAuthority.isFetched;
            const submittedCleaners = cleaners.filter(
              c =>
                getCleanerReviewMode(c, reviewStatusAuthority) === 'submitted'
            );
            if (submittedCleaners.length === 0 && !showSummarySpinner) {
              return null;
            }
            return (
              <div className="mb-6 flex flex-col gap-6 border-b border-gray-200 pb-6 dark:border-neutral-800">
                {showSummarySpinner ? (
                  <div className="flex justify-center py-10">
                    <Loader2 className="h-7 w-7 animate-spin text-[#ED7034]" />
                  </div>
                ) : (
                  submittedCleaners.map((cleaner, idx) => (
                    <CleanerReviewSubmitted
                      key={cleaner.booked_cleaner_id || idx}
                      cleaner={cleaner}
                    />
                  ))
                )}
              </div>
            );
          })()
        : null}

      {/* Footer */}
      <div
        className={cn(
          'flex flex-col gap-4 pt-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between'
        )}
      >
        <Link
          to="/bookings/$id"
          params={{ id: booking.booking_id }}
          search={viewDetailsSearch}
          onClick={() =>
            track('click', {
              eventAction: 'clickButton',
              eventCategory: 'bookingCard',
              eventDescription: 'clickViewDetails',
              eventLabel: 'bookingCardList',
            })
          }
          className="text-sm font-bold text-black no-underline hover:opacity-80 dark:text-white"
        >
          View Details
        </Link>
        {isCompletedRow ? (
          <div className="flex w-full flex-col gap-3 sm:ml-auto sm:w-auto sm:flex-row sm:flex-wrap">
            {canReportIssue ? (
              <Button
                asChild
                variant="outline"
                className="h-12 w-full rounded-full border-gray-300 px-6 text-sm font-bold sm:w-auto"
              >
                <Link
                  to="/bookings/$id"
                  params={{ id: booking.booking_id }}
                  search={{
                    ...viewDetailsSearch,
                    section: 'report',
                    showReportModal: 'true',
                  }}
                >
                  Report Issue
                </Link>
              </Button>
            ) : null}
            {canRateCleaner ? (
              <Button
                asChild
                className="h-12 w-full rounded-full bg-[#FF6B00] px-6 text-sm font-bold text-white shadow-none hover:bg-[#e66000] sm:w-auto"
              >
                <Link
                  to="/bookings/$id"
                  params={{ id: booking.booking_id }}
                  search={{
                    ...viewDetailsSearch,
                    section: 'review',
                    showReviewModal: 'true',
                  }}
                >
                  Rate Cleaner
                </Link>
              </Button>
            ) : null}
            <Button
              asChild
              className="h-12 w-full rounded-full bg-[#FF6B00] px-8 text-base font-bold text-white shadow-none hover:bg-[#e66000] sm:w-auto"
            >
              <Link
                to="/booking"
                search={{ book_again: booking.booking_id }}
                onClick={() =>
                  track('click', {
                    eventAction: 'clickButton',
                    eventCategory: 'bookButton',
                    eventDescription: 'clickBookAgain',
                    eventLabel: 'startBooking',
                    screenName: 'myCleaningsPage',
                    payload: {
                      city: booking.city,
                      state: booking.state,
                      cleaner: booking.cleaners?.[0]?.cleaner_id,
                      hours: booking.cleaners?.[0]?.no_of_hours,
                    },
                  })
                }
              >
                Book Again
              </Link>
            </Button>
          </div>
        ) : null}
      </div>
    </article>
  );
}

/** Merges booking detail when review/tip UI needs detail API (full forms or submitted summary). */
export function DashboardBookingCardWithMergedDetail({
  listBooking,
  variant,
  showReviewAndTip,
  showSubmittedReviewSummary = false,
  viewDetailsSearch,
}: {
  listBooking: Booking;
  variant: 'upcoming' | 'completed' | 'history';
  showReviewAndTip: boolean;
  showSubmittedReviewSummary?: boolean;
  viewDetailsSearch?: DashboardBookingViewDetailsSearch;
}) {
  const listCompleted = listBooking.status.toLowerCase() === 'completed';

  const listHintsSubmitted = (listBooking.cleaners ?? []).some(c =>
    hasSubmittedReviewOrTip(c)
  );

  const reviewStatusEnabled = listCompleted && !!listBooking.booking_id;

  const complianceEnabled = listCompleted && !!listBooking.booking_id;

  const {
    data: reviewStatus,
    isLoading: reviewStatusLoading,
    isFetched: reviewStatusFetched,
  } = useGetBookingReviewStatusQuery(listBooking.booking_id, {
    enabled: reviewStatusEnabled,
  });

  const { data: reviewExchange, isFetched: reviewExchangeFetched } =
    useGetBookingReviewExchangeQuery(listBooking.booking_id, {
      enabled: complianceEnabled,
    });

  const { data: issueReport, isFetched: issueReportFetched } =
    useGetBookingIssueReportQuery(listBooking.booking_id, {
      enabled: complianceEnabled,
    });

  const statusSaysSubmitted =
    reviewStatus?.cleaners?.some(c => c.review_submitted || c.tip_submitted) ??
    false;

  const detailEnabled =
    listCompleted &&
    !!listBooking.booking_id &&
    (showReviewAndTip ||
      (showSubmittedReviewSummary &&
        (listHintsSubmitted || (reviewStatusFetched && statusSaysSubmitted))));

  const { data: detail, isLoading } = useGetBookingDetailQuery(
    listBooking.booking_id,
    {
      enabled: detailEnabled,
    }
  );

  const reviewStatusAuthority: BookingReviewStatusAuthority | undefined =
    reviewStatusEnabled || complianceEnabled
      ? {
          isLoading: reviewStatusLoading,
          isFetched:
            reviewStatusFetched &&
            (!complianceEnabled ||
              (reviewExchangeFetched && issueReportFetched)),
          cleaners: reviewStatus?.cleaners,
          reviewWindowExpired: reviewExchange?.review_window_expired,
          issueReportSubmitted:
            issueReport?.submitted === true ||
            reviewExchange?.issue_report_submitted === true,
        }
      : undefined;

  const booking = useMemo(() => {
    if (!detailEnabled || !detail) return listBooking;
    return {
      ...listBooking,
      ...detail,
      total_amount: parseFloat(String(detail.total_amount)),
      cleaners: detail.cleaners?.length
        ? detail.cleaners
        : listBooking.cleaners,
    };
  }, [listBooking, detail, detailEnabled]);

  const showBlockingOverlay =
    Boolean(showReviewAndTip) && detailEnabled && isLoading && !detail;

  return (
    <div className="relative">
      {showBlockingOverlay ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/65 dark:bg-neutral-950/65">
          <Loader2 className="h-7 w-7 animate-spin text-[#ED7034]" />
        </div>
      ) : null}
      <DashboardBookingCard
        booking={booking}
        variant={variant}
        showReviewAndTip={showReviewAndTip}
        showSubmittedReviewSummary={showSubmittedReviewSummary}
        reviewStatusAuthority={reviewStatusAuthority}
        viewDetailsSearch={viewDetailsSearch}
      />
    </div>
  );
}
